<template>
	<v-app>
		<!-- App Bar -->
		<v-app-bar app elevation="1" class="app-header">
			<v-app-bar-nav-icon @click="onToggleDrawer"></v-app-bar-nav-icon>

			<!-- Logo clicável -->
			<router-link to="/dashboard" class="logo-link">
				<OriLogo :size="compactHeader ? 48 : 76" variant="full" />
			</router-link>

			<v-spacer></v-spacer>

			<!-- Trial Banner -->
			<v-chip
				v-if="trialDaysLeft !== null && !compactHeader"
				color="warning"
				variant="tonal"
				size="small"
				class="font-weight-medium mr-2 trial-chip"
				prepend-icon="mdi-timer-sand"
				@click="$router.push('/temple/plan')"
			>
				{{ $t('billing.trial.banner', { days: trialDaysLeft }) }}
			</v-chip>

			<!-- Atalhos do ritual ativo -->
			<div v-if="activeRitual && !compactHeader" class="active-ritual-shortcuts d-flex align-center mr-2">
				<v-chip
					:to="`/rituals/${activeRitual.id}`"
					color="success"
					variant="tonal"
					size="small"
					class="font-weight-medium active-ritual-chip"
				>
					<v-icon size="16" class="active-ritual-pulse">mdi-access-point</v-icon>
					<span class="active-ritual-title">{{ activeRitual.title }}</span>
				</v-chip>
				<v-tooltip location="bottom" :text="$t('ritualAttendance.attendance')">
					<template #activator="{ props: tp }">
						<v-btn
							v-bind="tp"
							:to="`/ritual/${activeRitual.id}/atendimento`"
							icon
							size="small"
							variant="text"
							color="success"
						>
							<v-icon size="20">mdi-card-account-details-outline</v-icon>
						</v-btn>
					</template>
				</v-tooltip>
				<v-tooltip location="bottom" :text="$t('ticketCall.title')">
					<template #activator="{ props: tp }">
						<v-btn
							v-bind="tp"
							:to="`/ritual/${activeRitual.id}/chamada`"
							icon
							size="small"
							variant="text"
							color="success"
						>
							<v-icon size="20">mdi-bullhorn-outline</v-icon>
						</v-btn>
					</template>
				</v-tooltip>
			</div>

			<v-divider
				v-if="!compactHeader && (activeRitual || trialDaysLeft !== null)"
				vertical class="mx-2 align-self-center" style="height: 28px" aria-hidden="true"
			/>

			<HeaderSearch />

			<!-- Seletores no header -->
			<div v-if="!compactHeader" class="header-selectors">
				<theme-selector />
				<language-selector />
			</div>

			<!-- User Avatar Menu -->
			<v-menu offset-y location="bottom end">
				<template #activator="{ props }">
					<v-btn v-bind="props" icon variant="text" class="ml-1">
						<v-avatar
							:color="authStore.user?.photoUrl ? undefined : 'primary'"
							size="34"
						>
							<v-img
								v-if="authStore.user?.photoUrl"
								:src="authStore.user.photoUrl"
								:alt="authStore.user?.displayName"
							/>
							<span v-else class="text-white text-caption font-weight-medium">
								{{ userInitials }}
							</span>
						</v-avatar>
					</v-btn>
				</template>

				<v-card min-width="220" rounded="lg" elevation="2">
					<div class="user-menu-header">
						<div class="user-menu-header__name">{{ authStore.user?.displayName }}</div>
						<div class="user-menu-header__email">{{ authStore.user?.email }}</div>
					</div>
					<v-divider />
					<div v-if="compactHeader" class="px-4 py-2" @click.stop>
						<div class="text-caption text-medium-emphasis">{{ $t('common.headerSearch.preferences') }}</div>
						<div class="d-flex align-center">
							<theme-selector />
							<language-selector />
						</div>
					</div>
					<v-list density="compact" nav>
						<template v-if="compactHeader">
							<v-list-item v-if="trialDaysLeft !== null" to="/temple/plan"
								prepend-icon="mdi-timer-sand" :title="$t('billing.trial.banner', { days: trialDaysLeft })" />
							<template v-if="activeRitual">
								<v-list-item :to="`/rituals/${activeRitual.id}`" prepend-icon="mdi-access-point" :title="activeRitual.title" />
								<v-list-item :to="`/ritual/${activeRitual.id}/atendimento`" prepend-icon="mdi-card-account-details-outline" :title="$t('ritualAttendance.attendance')" />
								<v-list-item :to="`/ritual/${activeRitual.id}/chamada`" prepend-icon="mdi-bullhorn-outline" :title="$t('ticketCall.title')" />
							</template>
						</template>
						<v-list-item
							prepend-icon="mdi-account-circle-outline"
							:title="$t('profile.title')"
							to="/profile"
						/>
						<v-list-item
							prepend-icon="mdi-logout"
							:title="$t('navigation.logout')"
							@click="logout"
						/>
					</v-list>
				</v-card>
			</v-menu>
		</v-app-bar>

		<!-- Navigation Drawer -->
		<v-navigation-drawer
			:model-value="drawer"
			@update:model-value="onDrawerUpdate"
			:permanent="!mobile"
			:temporary="mobile"
			app
			class="app-drawer"
		>
			<app-sidebar ref="sidebarRef" @close="onSidebarClose" />
		</v-navigation-drawer>

		<!-- Main Content -->
		<v-main class="main-content">
			<v-container fluid class="pa-4">
				<OwnershipTransferBanner />
				<slot />
			</v-container>
		</v-main>

		<!-- Footer -->
		<v-footer class="footer" app>
			<footer-help-button />
			<v-chip
				v-if="isHml"
				color="orange"
				size="x-small"
				variant="flat"
				class="ml-2 font-weight-bold"
			>
				HOMOLOGAÇÃO
			</v-chip>
			<v-spacer></v-spacer>
			<span class="footer-motto">Plante amor!</span>
			<span class="footer-divider">&middot;</span>
			<span class="footer-brand">ORI</span>
			<span class="footer-text">
				v{{ appVersion }} · {{ buildHash }}
				&mdash; &copy; {{ new Date().getFullYear() }}
			</span>
		</v-footer>

		<!-- Sistema global de notificações -->
		<global-notifications />

		<!-- Auto-trigger de tours contextuais -->
		<tour-auto-trigger />

		<!-- Assistente virtual (chatbot de ajuda) -->
		<chat-widget />
	</v-app>
</template>

<script setup lang="ts">
import { ref, computed, provide, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { billingService, type BillingStatus } from "@/services/billingService";
import { useDisplay } from "vuetify";
import { socketService } from "@/services/socketService";
import {
	notifyServerEntityEvent,
	notifyAllEntitiesChanged,
	type ServerEntityEvent,
} from "@/composables/useEntityRefresh";
import HeaderSearch from "@/components/layout/HeaderSearch.vue";
import AppSidebar from "@/components/layout/AppSidebar.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import ThemeSelector from "@/components/common/ThemeSelector.vue";
import OriLogo from "@/components/common/OriLogo.vue";
import GlobalNotifications from "@/components/common/GlobalNotifications.vue";
import FooterHelpButton from "@/components/common/FooterHelpButton.vue";
import TourAutoTrigger from "@/components/common/TourAutoTrigger.vue";
import ChatWidget from "@/components/chat/ChatWidget.vue";
import OwnershipTransferBanner from "@/components/ownership-transfer/OwnershipTransferBanner.vue";
import { ticketService } from "@/services/ticketService";
import type { ActiveRitual } from "@/types/ticket";

// Version (injected at build time by Vite)
const appVersion = __APP_VERSION__;
const buildHash = __BUILD_HASH__;
const isHml = import.meta.env.VITE_APP_ENV === "hml";

// Composables
const authStore = useAuthStore();
const { mobile, smAndDown: compactHeader } = useDisplay();

// Refs
const drawer = ref(!mobile.value); // Inicialmente aberto no desktop, fechado no mobile
const sidebarRef = ref<InstanceType<typeof AppSidebar> | null>(null);
const activeRitual = ref<ActiveRitual | null>(null);
const billingStatus = ref<BillingStatus | null>(null);

const userInitials = computed(() => {
	const name = authStore.user?.displayName || "";
	const parts = name.trim().split(/\s+/);
	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const trialDaysLeft = computed(() => {
	if (billingStatus.value?.status !== "TRIALING" || !billingStatus.value?.trialEndsAt) return null;
	const diff = new Date(billingStatus.value.trialEndsAt).getTime() - Date.now();
	const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
	return days;
});

// Tour active flag — prevents drawer close during guided tour on mobile
const tourActive = ref(false);

// Provide refs for guided tour
provide("sidebarRef", sidebarRef);
provide("drawerRef", drawer);
provide("tourActiveRef", tourActive);

// Active ritual — load on mount + listen for WebSocket events
async function loadActiveRitual() {
	try {
		const rituals = await ticketService.getActiveRituals();
		activeRitual.value = rituals.length > 0 ? rituals[0] : null;
	} catch {
		activeRitual.value = null;
	}
}

function onRitualStatusChanged() {
	loadActiveRitual();
}

// Cadastro mudou em outra aba ou na mão de outro dirigente: as telas que ouvem
// aquele domínio recarregam sozinhas (FEAT-044). O eco da própria aba é
// descartado pelo id do cliente.
function onEntityChangedEvent(event: ServerEntityEvent) {
	notifyServerEntityEvent(event);
}

// O socket voltou: o que mudou enquanto ele estava fora não gerou evento
// nenhum, então as telas abertas recarregam por garantia.
function onSocketReconnect() {
	loadActiveRitual();
	notifyAllEntitiesChanged();
}

onMounted(() => {
	loadActiveRitual();
	billingService.getStatus().then((s) => (billingStatus.value = s)).catch(() => {});
	socketService.connect();
	socketService.on("ritual.statusChanged", onRitualStatusChanged);
	socketService.on("entidade.alterada", onEntityChangedEvent);
	socketService.onReconnect(onSocketReconnect);
});

onUnmounted(() => {
	socketService.off("ritual.statusChanged", onRitualStatusChanged);
	socketService.off("entidade.alterada", onEntityChangedEvent);
	socketService.offReconnect(onSocketReconnect);
	socketService.disconnect();
});

// Drawer control — block close during guided tour on mobile
const onDrawerUpdate = (value: boolean) => {
	if (!value && tourActive.value) return; // block close during tour
	drawer.value = value;
};

const onSidebarClose = () => {
	if (tourActive.value) return; // block close during tour
	drawer.value = false;
};

const onToggleDrawer = () => {
	if (tourActive.value) return; // block toggle during tour
	drawer.value = !drawer.value;
};

// Methods
const logout = () => {
	authStore.logout();
};
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;

.v-application {
	display: flex;
	flex-direction: column;
	min-height: 100vh;
}

// ============================================
// HEADER
// ============================================
// O painel da busca precisa ultrapassar a altura do cabeçalho.
.app-header :deep(.v-toolbar__content) {
	overflow: visible;
}

.app-header {
	background: rgb(var(--v-theme-surface)) !important;
	border-bottom: 1px solid rgba(var(--v-theme-primary), 0.06);
	transition: border-color 0.3s ease;
}

// ============================================
// DRAWER
// ============================================
.app-drawer {
	border-right: 1px solid rgba(var(--v-theme-primary), 0.08) !important;
}

// ============================================
// MAIN CONTENT
// ============================================
.main-content {
	flex: 1 0 auto;
	position: relative;
	background:
		// Brilho radial do secondary no canto superior direito
		radial-gradient(
			ellipse 70% 55% at 92% 3%,
			rgba(var(--v-theme-secondary), 0.14) 0%,
			transparent 65%
		),
		// Degradê vertical principal
		linear-gradient(
			to bottom,
			rgba(var(--v-theme-primary), 0.12) 0%,
			rgba(var(--v-theme-primary), 0.05) 35%,
			transparent 60%
		),
		// Toque diagonal — cruza primary com secondary
		linear-gradient(
			135deg,
			transparent 0%,
			rgba(var(--v-theme-secondary), 0.06) 45%,
			transparent 75%
		),
		rgb(var(--v-theme-background));
}

// ============================================
// FOOTER
// ============================================
.footer {
	background-color: rgb(var(--v-theme-surface));
	border-top: 1px solid rgba(var(--v-theme-primary), 0.12);
	padding: 0 vars.$spacing-md;
	height: 36px !important;
	display: flex;
	align-items: center;
	position: relative;
	bottom: 0;
	width: 100%;
	z-index: 1;
}

.footer-motto {
	font-family: vars.$font-secondary;
	font-size: 0.7rem;
	font-style: italic;
	color: rgb(var(--v-theme-primary));
	opacity: 0.7;
}

.footer-divider {
	margin: 0 0.5rem;
	color: rgb(var(--v-theme-on-surface));
	opacity: 0.3;
}

.footer-brand {
	font-family: vars.$font-heading;
	font-size: 0.75rem;
	font-weight: 700;
	color: rgb(var(--v-theme-primary));
	margin-right: 0.5rem;
	letter-spacing: 0.05em;
}

.footer-text {
	font-size: 0.7rem;
	color: rgb(var(--v-theme-on-surface));
	opacity: 0.45;
	font-family: vars.$font-secondary;
}

// ============================================
// HEADER ELEMENTS
// ============================================
.logo-link {
	text-decoration: none;
	color: inherit;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.8;
	}
}

.header-selectors {
	display: flex;
	align-items: center;
	gap: 8px;
}

.trial-chip {
	cursor: pointer;
}

.active-ritual-shortcuts {
	gap: 2px;
}

.active-ritual-chip {
	--ping-ring-color: rgba(var(--v-theme-success), 0.5);
	animation: pingRing 3s ease-out infinite;

	:deep(.v-chip__content) {
		display: flex;
		align-items: center;
		gap: 6px;
	}
}

.active-ritual-pulse {
	animation: pulse 1.4s ease-in-out infinite;
}

.active-ritual-title {
	max-width: 160px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

@media (max-width: 600px) {
	.active-ritual-title {
		max-width: 90px;
	}
}

// ============================================
// USER AVATAR MENU
// ============================================
.user-menu-header {
	padding: 12px 16px;

	&__name {
		font-size: 0.875rem;
		font-weight: 600;
		font-family: vars.$font-secondary;
		color: rgb(var(--v-theme-on-surface));
	}

	&__email {
		font-size: 0.75rem;
		color: rgb(var(--v-theme-on-surface));
		opacity: 0.6;
	}
}
</style>
